import * as React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router';

import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { wbLazy } from '@workbuzz/shared-ui';
import { useCleanPosthogCookie } from 'shared/hooks/useCleanPosthogCookie';
import { useRemoveAuthTokenCookieAndLogoutOnMount } from 'shared/hooks/useRemoveAuthTokenCookieAndLogoutOnMount';
import { useUpdateAuthTokenCookie } from 'shared/hooks/useUpdateAuthTokenCookie';
import {
  LOGIN_PATTERN,
  OAUTH_CALLBACK,
  RESET_PASSWORD,
  RESET_PASSWORD_REQUEST,
  ROOT,
  SESSIONS,
  SET_PASSWORD,
  SETUP_MFA,
  VERIFY_MFA,
} from 'app/route';
import { FeatureFlags } from 'config/featureFlags';
import { PageWrapper } from 'component/PageWrapper/PageWrapper';
import { PageWrapperWithFeedback } from 'component/PageWrapperWithFeedback/PageWrapperWithFeedback';
import { useInitializeRUM } from 'shared/hooks/useInitializeRUM';
import { useInitializeDataDogLogs } from 'shared/hooks/useInitializeDataDogLogs';

const LoginFormPage = wbLazy(() =>
  import(/* webpackChunkName: "LoginFormPage" */ 'view/LoginFormPage/LoginFormPage').then(
    (module) => ({
      default: module['LoginFormPage'],
    })
  )
);

const ResetPasswordRequestPage = wbLazy(() =>
  import(
    /* webpackChunkName: "ResetPasswordRequestPage" */ 'view/ResetPasswordRequestPage/ResetPasswordRequestPage'
  ).then((module) => ({
    default: module['ResetPasswordRequestPage'],
  }))
);

const ResetPasswordPage = wbLazy(() =>
  import(
    /* webpackChunkName: "ResetPasswordPage" */ 'view/ResetPasswordPage/ResetPasswordPage'
  ).then((module) => ({
    default: module['ResetPasswordPage'],
  }))
);

const SetPasswordPage = wbLazy(() =>
  import(/* webpackChunkName: "SetPasswordPage" */ 'view/SetPasswordPage/SetPasswordPage').then(
    (module) => ({
      default: module['SetPasswordPage'],
    })
  )
);

const SetupMfaPage = wbLazy(() =>
  import(/* webpackChunkName: "SetupMfaPage" */ 'view/SetupMfaPage/SetupMfaPage').then(
    (module) => ({
      default: module['SetupMfaPage'],
    })
  )
);

const VerifyMfaPage = wbLazy(() =>
  import(/* webpackChunkName: "VerifyMfaPage" */ 'view/VerifyMfaPage/VerifyMfaPage').then(
    (module) => ({
      default: module['VerifyMfaPage'],
    })
  )
);

const OAuthCallbackPage = wbLazy(() =>
  import(
    /* webpackChunkName: "OAuthCallbackPage" */ 'view/OAuthCallbackPage/OAuthCallbackPage'
  ).then((module) => ({
    default: module['OAuthCallbackPage'],
  }))
);

const ActiveSessionsPage = wbLazy(() =>
  import(
    /* webpackChunkName: "ActiveSessionsPage" */ 'view/ActiveSessionsPage/ActiveSessionsPage'
  ).then((module) => ({
    default: module['ActiveSessionsPage'],
  }))
);

export const AppView: React.VFC = () => {
  const isG2ReviewEnabled = useFeatureIsOn('g2-review-enabled');
  const isActiveSessionsEnabled = useFeatureIsOn(FeatureFlags.ACTIVE_SESSIONS);
  const PageWrapperToUse = isG2ReviewEnabled ? PageWrapperWithFeedback : PageWrapper;

  // The sessions page is the only authenticated route and must keep the session it was opened with.
  const isOnSessionsRoute = !!useRouteMatch(SESSIONS);
  const isAuthenticatedRoute = isActiveSessionsEnabled && isOnSessionsRoute;

  useInitializeRUM();
  useInitializeDataDogLogs();
  useCleanPosthogCookie();
  useUpdateAuthTokenCookie();
  useRemoveAuthTokenCookieAndLogoutOnMount(!isAuthenticatedRoute);

  return (
    <Switch>
      {isActiveSessionsEnabled && <Route path={SESSIONS} component={ActiveSessionsPage} />}
      <Route
        render={() => (
          <PageWrapperToUse>
            <Switch>
              <Route path={LOGIN_PATTERN} component={LoginFormPage} />
              <Route path={RESET_PASSWORD_REQUEST} component={ResetPasswordRequestPage} />
              <Route path={SET_PASSWORD} component={SetPasswordPage} />
              <Route path={RESET_PASSWORD} component={ResetPasswordPage} />
              <Route path={SETUP_MFA} component={SetupMfaPage} />
              <Route path={VERIFY_MFA} component={VerifyMfaPage} />
              <Route path={OAUTH_CALLBACK} component={OAuthCallbackPage} />
              <Route path={ROOT} exact component={LoginFormPage} />
              <Route render={() => <div data-testid="notFoundPage">Not found</div>} />
            </Switch>
          </PageWrapperToUse>
        )}
      />
    </Switch>
  );
};
